/**
 * PlannedLayerSidebar Component
 *
 * 桌面版右側邊欄，顯示計劃任務並支持拖曳到日曆。
 * 使用 sticky 定位，讓側邊欄在日曆滾動時保持可見。
 *
 * 功能：
 * - 按日期分組顯示任務（Today, This Week, Overdue 等）
 * - 支持拖曳任務到日曆進行排程
 * - 點擊任務查看詳情
 */
import React, { useCallback } from 'react';
import { Task } from '../../../types/task';
import { usePlannedTasks, PlannedTaskGroups } from '../../../hooks/usePlannedTasks';
import PlannedTaskGroup from './PlannedTaskGroup';
import {
  Calendar,
  CalendarClock,
  AlertCircle,
  CalendarDays,
  ClipboardList,
  Inbox,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react';

// 側邊欄寬度常數
const SIDEBAR_WIDTH_EXPANDED = 280;    // 桌面版展開寬度
const SIDEBAR_WIDTH_TABLET = 200;      // 平板版展開寬度
const SIDEBAR_WIDTH_COLLAPSED = 48;    // 收合寬度（圖示條）

export interface PlannedLayerSidebarProps {
  /** 拖曳開始回調（滑鼠） */
  onDragStart?: (task: Task, e: React.DragEvent) => void;
  /** 拖曳結束回調 */
  onDragEnd?: () => void;
  /** 觸控拖曳開始回調（平板） */
  onTouchStart?: (task: Task, e: React.TouchEvent) => void;
  /** 點擊查看任務詳情 */
  onViewTaskDetail?: (task: Task) => void;
  /** 是否收合 */
  isCollapsed?: boolean;
  /** 切換收合狀態 */
  onToggleCollapse?: () => void;
  /** 高亮指定 Task ID */
  highlightTaskId?: string | null;
  /** 清除高亮回調 */
  onClearHighlight?: () => void;
}

/**
 * 獲取分組配置
 */
function getGroupConfigs(groups: PlannedTaskGroups) {
  return [
    {
      key: 'today',
      title: 'Today',
      tasks: groups.today,
      icon: <Calendar className="w-4 h-4" />,
      countClassName: 'bg-sky-100 text-sky-700',
      defaultExpanded: true,
    },
    {
      key: 'thisWeek',
      title: 'This Week',
      tasks: groups.thisWeek,
      icon: <CalendarDays className="w-4 h-4" />,
      countClassName: 'bg-green-100 text-green-700',
      defaultExpanded: true,
    },
    {
      key: 'overdue',
      title: 'Overdue',
      tasks: groups.overdue,
      icon: <AlertCircle className="w-4 h-4" />,
      countClassName: 'bg-red-100 text-red-700',
      defaultExpanded: true,
    },
    {
      key: 'thisMonth',
      title: 'This Month',
      tasks: groups.thisMonth,
      icon: <CalendarClock className="w-4 h-4" />,
      countClassName: 'bg-sky-100 text-sky-700',
      defaultExpanded: false,
    },
    {
      key: 'later',
      title: 'Later',
      tasks: groups.later,
      icon: <ClipboardList className="w-4 h-4" />,
      countClassName: 'bg-gray-100 text-gray-600',
      defaultExpanded: false,
    },
    {
      key: 'other',
      title: 'Other',
      tasks: groups.other,
      icon: <Inbox className="w-4 h-4" />,
      countClassName: 'bg-gray-100 text-gray-500',
      defaultExpanded: false,
    },
  ];
}

const PlannedLayerSidebar: React.FC<PlannedLayerSidebarProps> = ({
  onDragStart,
  onDragEnd,
  onTouchStart,
  onViewTaskDetail,
  isCollapsed = false,
  onToggleCollapse,
  highlightTaskId,
  onClearHighlight,
}) => {
  const { groups, totalCount, isLoading, isEmpty } = usePlannedTasks();

  // 處理拖曳開始（滑鼠）
  const handleDragStart = useCallback(
    (task: Task, e: React.DragEvent) => {
      onDragStart?.(task, e);
    },
    [onDragStart]
  );

  // 處理拖曳結束
  const handleDragEnd = useCallback(() => {
    onDragEnd?.();
  }, [onDragEnd]);

  // 處理觸控開始（平板）
  const handleTouchStart = useCallback(
    (task: Task, e: React.TouchEvent) => {
      onTouchStart?.(task, e);
    },
    [onTouchStart]
  );

  // 處理查看詳情
  const handleViewDetail = useCallback(
    (task: Task) => {
      onViewTaskDetail?.(task);
    },
    [onViewTaskDetail]
  );

  // 分組配置
  const groupConfigs = getGroupConfigs(groups);

  // 收合狀態：顯示圖示條
  if (isCollapsed) {
    return (
      <div
        className="h-full bg-white flex flex-col items-center rounded-2xl shadow-sm border border-gray-100"
        style={{ width: SIDEBAR_WIDTH_COLLAPSED }}
      >
        {/* 展開按鈕 */}
        <button
          onClick={onToggleCollapse}
          className="w-full h-12 flex items-center justify-center hover:bg-gray-50 transition-colors"
          aria-label="展開任務面板"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>

        {/* 任務數量徽章（垂直排列） */}
        <div className="flex-1 flex flex-col items-center gap-3 py-4">
          {groups.today.length > 0 && (
            <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center">
              <span className="text-xs font-semibold text-sky-700">
                {groups.today.length}
              </span>
            </div>
          )}
          {groups.thisWeek.length > 0 && (
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <span className="text-xs font-semibold text-green-700">
                {groups.thisWeek.length}
              </span>
            </div>
          )}
          {groups.overdue.length > 0 && (
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
              <span className="text-xs font-semibold text-red-700">
                {groups.overdue.length}
              </span>
            </div>
          )}
          {groups.thisMonth.length > 0 && (
            <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center">
              <span className="text-xs font-semibold text-sky-700">
                {groups.thisMonth.length}
              </span>
            </div>
          )}
          {groups.later.length > 0 && (
            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
              <span className="text-xs font-semibold text-gray-600">
                {groups.later.length}
              </span>
            </div>
          )}
          {groups.other.length > 0 && (
            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
              <span className="text-xs font-semibold text-gray-500">
                {groups.other.length}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // 展開狀態：顯示完整側邊欄
  return (
    <div
      className="h-full w-full bg-white flex flex-col overflow-hidden flex-shrink-0 rounded-2xl shadow-sm border border-gray-100"
    >
      {/* 標題欄 */}
      <div className="flex-shrink-0 flex items-center justify-between p-4 bg-white">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-sky-600" />
          <span className="font-medium text-gray-800">Tasks</span>
          {totalCount > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">
              {totalCount}
            </span>
          )}
        </div>

        {/* 收合按鈕 */}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
            aria-label="收合任務面板"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        )}
      </div>

      {/* 內容區 */}
      <div className="flex-1 min-h-0 overflow-y-auto hide-scrollbar p-3">
        {/* Loading 狀態 */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-sky-500 border-t-transparent" />
          </div>
        )}

        {/* 空狀態 */}
        {!isLoading && isEmpty && (
          <div className="text-center py-8">
            <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No tasks</p>
            <p className="text-xs text-gray-400 mt-1">
              Set a plan date on tasks to see them here
            </p>
          </div>
        )}

        {/* 任務分組 */}
        {!isLoading && !isEmpty && (
          <div>
            {/* 拖曳提示 */}
            <div className="text-xs text-gray-500 mb-4 px-1">
              Drag tasks to calendar to schedule
            </div>

            {groupConfigs.map((config) => (
              <PlannedTaskGroup
                key={config.key}
                title={config.title}
                tasks={config.tasks}
                icon={config.icon}
                countClassName={config.countClassName}
                defaultExpanded={config.defaultExpanded}
                draggable={true}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onTouchStart={handleTouchStart}
                onViewDetail={handleViewDetail}
                highlightTaskId={highlightTaskId}
                onClearHighlight={onClearHighlight}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlannedLayerSidebar;
